import { GestureController, Gesture, GestureDetail } from '@ionic/angular';

const SWIPE_THRESHOLD = 80;           // pixels to the right to trigger dismiss
const VELOCITY_THRESHOLD = 0.4;       // horizontal velocity
const MAX_VERTICAL_DRIFT = 60;        // ignore if too much vertical movement
const RESISTANCE = 0.6;               // how much the modal resists being dragged
const VERTICAL_SWIPE_THRESHOLD = 120; // pixels down before dismissing
const VERTICAL_VELOCITY_THRESHOLD = 0.5;
const MAX_HORIZONTAL_DRIFT = 80;
const VERTICAL_RESISTANCE = 0.72;
// Keep the dismiss gesture in a small grab area at the top of the modal. A
// downward scroll that starts in the content must belong to ion-content.
const TOP_DISMISS_ZONE = 76;

let activeGestures = new WeakMap<HTMLIonModalElement, Gesture>();
let activeVerticalGestures = new WeakMap<HTMLIonModalElement, Gesture>();

/**
 * Attaches a custom Left-to-Right swipe-to-dismiss gesture on a modal.
 * This is useful when Ionic's built-in swipeToClose doesn't work
 * (e.g. because of heavy custom CSS like .detailsInModal).
 *
 * The gesture:
 * - Only cares about horizontal movement (left → right)
 * - Gives live visual feedback (translateX + slight opacity)
 * - Dismisses the modal if the user swipes far enough or fast enough
 * - Smoothly animates back if the swipe is cancelled
 */
export function enableLeftToRightSwipeDismiss(
  modal: HTMLIonModalElement,
  gestureCtrl: GestureController
): void {
  if (!modal || activeGestures.has(modal)) {
    return;
  }

  // Try to find the actual visual wrapper (important for custom styled modals)
  const wrapper =
    (modal.shadowRoot?.querySelector('.modal-wrapper') as HTMLElement) ||
    (modal.querySelector('.modal-wrapper') as HTMLElement) ||
    modal;

  const originalTransition = wrapper.style.transition;

  let startX = 0;
  let startY = 0;
  let currentDelta = 0;

  const gesture: Gesture = gestureCtrl.create({
    el: modal,
    gestureName: 'ltr-modal-dismiss',
    direction: 'x',
    threshold: 15,
    passive: false,
    onStart: (detail: GestureDetail) => {
      startX = detail.currentX;
      startY = detail.currentY;
      currentDelta = 0;

      // Kill any running transition so we can drag smoothly
      wrapper.style.transition = 'none';
    },
    onMove: (detail: GestureDetail) => {
      const deltaX = detail.currentX - startX;
      const deltaY = Math.abs(detail.currentY - startY);

      // Only allow mostly-horizontal rightward swipes
      if (deltaY > MAX_VERTICAL_DRIFT) {
        return;
      }

      // Only positive (rightward) movement
      if (deltaX <= 0) {
        currentDelta = 0;
        wrapper.style.transform = 'translateX(0)';
        wrapper.style.opacity = '1';
        return;
      }

      currentDelta = deltaX * RESISTANCE;

      wrapper.style.transform = `translateX(${currentDelta}px)`;
      wrapper.style.opacity = `${Math.max(0.6, 1 - currentDelta / 400)}`;
    },
    onEnd: (detail: GestureDetail) => {
      const deltaX = detail.currentX - startX;
      const velocityX = detail.velocityX;

      const shouldDismiss =
        deltaX > SWIPE_THRESHOLD || velocityX > VELOCITY_THRESHOLD;

      wrapper.style.transition =
        originalTransition || 'transform 0.22s cubic-bezier(0.32, 0.72, 0, 1), opacity 0.22s ease';

      if (shouldDismiss) {
        // Nice exit animation then dismiss
        wrapper.style.transform = `translateX(${Math.max(deltaX, 120)}px)`;
        wrapper.style.opacity = '0';

        // Give the animation a moment to play before actually dismissing
        setTimeout(() => {
          modal.dismiss(undefined, 'swipe-left-to-right').catch(() => {});
        }, 120);
      } else {
        // Animate back to rest position
        wrapper.style.transform = 'translateX(0)';
        wrapper.style.opacity = '1';
      }

      // Reset after animation
      setTimeout(() => {
        if (wrapper) {
          wrapper.style.transition = originalTransition || '';
          wrapper.style.transform = '';
          wrapper.style.opacity = '';
        }
      }, 280);
    },
  });

  gesture.enable(true);
  activeGestures.set(modal, gesture);

  // Auto-cleanup when the modal is dismissed by any means
  const cleanup = () => {
    const g = activeGestures.get(modal);
    if (g) {
      g.destroy();
      activeGestures.delete(modal);
    }
    modal.removeEventListener('ionModalDidDismiss', cleanup);
  };

  modal.addEventListener('ionModalDidDismiss', cleanup, { once: true });
}

/**
 * Restores the old sheet-style interaction for regular Ionic modals:
 * drag from top to bottom, see the modal move with the finger, then dismiss
 * after the threshold (or animate back when the gesture is cancelled).
 *
 * `canDismiss` is intentionally still used by Ionic as the final permission
 * check. It does not create this gesture by itself.
 */
export function enableTopToBottomSwipeDismiss(
  modal: HTMLIonModalElement,
  gestureCtrl: GestureController
): void {
  if (!modal || activeVerticalGestures.has(modal)) {
    return;
  }

  // Ionic sheet modals already own this gesture through breakpoints. A second
  // gesture would fight with Ionic's native sheet animation.
  if (Array.isArray((modal as any).breakpoints) && (modal as any).breakpoints.length > 0) {
    return;
  }

  const wrapper =
    (modal.shadowRoot?.querySelector('.modal-wrapper') as HTMLElement) ||
    (modal.querySelector('.modal-wrapper') as HTMLElement) ||
    modal;
  const backdrop = modal.shadowRoot?.querySelector('.modal-backdrop') as HTMLElement | null;
  const originalTransition = wrapper.style.transition;
  let startX = 0;
  let startY = 0;
  let currentDelta = 0;

  const canStartFromTop = (detail: GestureDetail): boolean => {
    const modalBounds = modal.getBoundingClientRect();
    return detail.currentY >= modalBounds.top &&
      detail.currentY <= modalBounds.top + TOP_DISMISS_ZONE;
  };

  const gesture = gestureCtrl.create({
    el: modal,
    gestureName: 'top-to-bottom-modal-dismiss',
    direction: 'y',
    threshold: 12,
    passive: false,
    // Do not capture touches that begin in the scrollable body. This is the
    // important distinction between dismissing from the grab area and simply
    // scrolling the modal content.
    canStart: canStartFromTop,
    onStart: (detail: GestureDetail) => {
      startX = detail.currentX;
      startY = detail.currentY;
      currentDelta = 0;
      wrapper.style.transition = 'none';
    },
    onMove: (detail: GestureDetail) => {
      const deltaY = detail.currentY - startY;
      const deltaX = Math.abs(detail.currentX - startX);

      // A diagonal/horizontal movement belongs to another gesture or to the
      // page content; do not drag the modal in that case.
      if (deltaX > MAX_HORIZONTAL_DRIFT || deltaY <= 0) {
        currentDelta = 0;
        wrapper.style.transform = 'translateY(0)';
        wrapper.style.opacity = '1';
        if (backdrop) backdrop.style.opacity = '';
        return;
      }

      currentDelta = deltaY * VERTICAL_RESISTANCE;
      wrapper.style.transform = `translateY(${currentDelta}px)`;
      wrapper.style.opacity = `${Math.max(0.45, 1 - currentDelta / 500)}`;
      if (backdrop) {
        backdrop.style.opacity = `${Math.max(0.15, 1 - currentDelta / 360)}`;
      }
    },
    onEnd: (detail: GestureDetail) => {
      const deltaY = detail.currentY - startY;
      const velocityY = (detail as any).velocityY || 0;
      const shouldDismiss =
        deltaY > VERTICAL_SWIPE_THRESHOLD || velocityY > VERTICAL_VELOCITY_THRESHOLD;

      wrapper.style.transition =
        originalTransition || 'transform 0.26s cubic-bezier(0.32, 0.72, 0, 1), opacity 0.22s ease';

      if (shouldDismiss) {
        const exitDistance = Math.max(deltaY, 180);
        wrapper.style.transform = `translateY(${exitDistance}px)`;
        wrapper.style.opacity = '0';
        if (backdrop) backdrop.style.opacity = '0';
        setTimeout(() => {
          modal.dismiss(undefined, 'swipe-top-to-bottom').catch(() => {
            // canDismiss may reject the dismissal; restore the modal visibly.
            wrapper.style.transform = 'translateY(0)';
            wrapper.style.opacity = '1';
            if (backdrop) backdrop.style.opacity = '';
          });
        }, 150);
      } else {
        wrapper.style.transform = 'translateY(0)';
        wrapper.style.opacity = '1';
        if (backdrop) backdrop.style.opacity = '';
      }

      setTimeout(() => {
        wrapper.style.transition = originalTransition || '';
        wrapper.style.transform = '';
        wrapper.style.opacity = '';
        if (backdrop) backdrop.style.opacity = '';
      }, 320);
    },
  });

  gesture.enable(true);
  activeVerticalGestures.set(modal, gesture);

  const cleanup = () => {
    const active = activeVerticalGestures.get(modal);
    if (active) {
      active.destroy();
      activeVerticalGestures.delete(modal);
    }
    modal.removeEventListener('ionModalDidDismiss', cleanup);
  };

  modal.addEventListener('ionModalDidDismiss', cleanup, { once: true });
}

/**
 * Sets up a global listener that automatically enables Left-to-Right
 * swipe-to-dismiss on EVERY modal that gets presented in the app.
 *
 * This is the "set it and forget it" solution.
 * Call this once (best place is AppComponent).
 */
export function setupGlobalLeftToRightModalDismiss(gestureCtrl: GestureController): void {
  document.addEventListener('ionModalDidPresent', (event: Event) => {
    const modal = event.target as HTMLIonModalElement;
    if (modal && modal.tagName === 'ION-MODAL') {
      // Small delay so the DOM/shadow DOM is fully ready
      requestAnimationFrame(() => {
        enableLeftToRightSwipeDismiss(modal, gestureCtrl);
        enableTopToBottomSwipeDismiss(modal, gestureCtrl);
      });
    }
  });
}
